// useVentaFormXL - Sprint 44 Fase 3 Vista 4.
// Hook del formulario de venta XL. Reusa el ventasService legacy.
// Funcionalidad: carga por :id o blank, save (POST/PUT), emitir (dispara
// kardex backend), anular, refresh. msg con auto-dismiss 4s.
// Contrato backend 100% preservado.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as svc from '../../ventas/services/ventasService';

const MSG_AUTO_DISMISS_MS = 4000;

const EMPTY = {
  id: null,
  numero: '',
  cliente_id: null,
  fecha: '',
  moneda: 'COP',
  condicion_pago: 'contado',
  observaciones: '',
  estado: 'borrador',
  total: 0,
  subtotal: 0,
  iva_total: 0,
  precio_incluye_iva: false,
  cotizacion_id: null,
  fecha_emision: null,
  created_at: null,
  updated_at: null,
  usuario_creacion: null,
  cliente: null,
};

export default function useVentaFormXL() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'nueva';

  const [form, setForm]     = useState(EMPTY);
  const [tab, setTab]       = useState('datos');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [emitting, setEmitting] = useState(false);
  const [acting, setActing] = useState(false);
  const [msg, setMsg]       = useState(null);

  const msgTimer = useRef(null);
  const pushMsg = useCallback((kind, text) => {
    if (msgTimer.current) clearTimeout(msgTimer.current);
    setMsg({ kind, text });
    msgTimer.current = setTimeout(() => setMsg(null), MSG_AUTO_DISMISS_MS);
  }, []);
  useEffect(() => () => {
    if (msgTimer.current) clearTimeout(msgTimer.current);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (isNew) {
        try {
          const numero = await svc.nextNumero();
          if (alive) setForm({
            ...EMPTY,
            numero,
            fecha: new Date().toISOString().slice(0, 10),
          });
        } catch (_) {
          if (alive) setForm({
            ...EMPTY,
            fecha: new Date().toISOString().slice(0, 10),
          });
        }
        if (alive) setLoaded(true);
        return;
      }
      try {
        const d = await svc.getVenta(id);
        if (alive) setForm({ ...EMPTY, ...(d || {}) });
      } catch (e) {
        if (alive) pushMsg('err', 'No se pudo cargar la venta');
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [id, isNew, pushMsg]);

  const set = useCallback((k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
  }, []);

  const buildPayload = useCallback(() => {
    const p = { ...form };
    if (!p.fecha) p.fecha = null;
    delete p.cliente;
    return p;
  }, [form]);

  const onSave = useCallback(async () => {
    if (!form.cliente_id) {
      pushMsg('err', 'Selecciona un cliente');
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isNew) {
        const created = await svc.createVenta(payload);
        pushMsg('ok', 'Venta creada en borrador');
        if (created && created.id) navigate('/ventas/' + created.id, { replace: true });
      } else {
        const updated = await svc.updateVenta(id, payload);
        if (updated) setForm((f) => ({ ...f, ...updated }));
        pushMsg('ok', 'Venta actualizada');
      }
    } catch (e) {
      pushMsg('err', (e && e.message) || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  }, [isNew, id, form.cliente_id, buildPayload, navigate, pushMsg]);

  const onEmitir = useCallback(async () => {
    if (isNew) return;
    if (!window.confirm('Emitir la venta? Esto descuenta stock en el kardex y no se puede editar.')) return;
    setEmitting(true);
    try {
      const r = await svc.emitir(id);
      if (r && r.venta) setForm((f) => ({ ...f, ...r.venta }));
      pushMsg('ok', 'Venta emitida y stock descontado');
    } catch (e) {
      pushMsg('err', (e && e.message) || 'Error al emitir');
    } finally {
      setEmitting(false);
    }
  }, [isNew, id, pushMsg]);

  const onAnular = useCallback(async () => {
    if (isNew) return;
    if (!window.confirm('Anular la venta? (El kardex no se revierte automaticamente)')) return;
    setActing(true);
    try {
      const r = await svc.anular(id);
      if (r) setForm((f) => ({ ...f, ...r }));
      pushMsg('ok', 'Venta anulada');
    } catch (e) {
      pushMsg('err', (e && e.message) || 'Error');
    } finally {
      setActing(false);
    }
  }, [isNew, id, pushMsg]);

  const onRefresh = useCallback(async () => {
    if (isNew) return;
    try {
      const d = await svc.getVenta(id);
      if (d) setForm((f) => ({ ...f, ...d }));
    } catch (_) { /* no bloquea */ }
  }, [isNew, id]);

  return {
    id, isNew, navigate,
    form, set, tab, setTab,
    loaded, saving, emitting, acting, msg,
    onSave, onEmitir, onAnular, onRefresh,
    clearMsg: () => setMsg(null),
  };
}
